import { z } from 'zod';
/**
 * Event types the candidate's own machine observes — and therefore the only ones the API
 * may accept from a candidate socket.
 *
 * What this list encodes is *provenance*, not severity: everything here is something the
 * watchdog or the embedded browser saw happen on the machine under observation, so the
 * candidate's client is the authoritative reporter. Anything the *server* authors about the
 * session (`serverAuthoredTelemetryEventTypeSchema`) must never be constructible by that
 * client: the evidence log exists to adjudicate cheating, and a candidate able to forge a
 * row attributed to an interviewer could manufacture their own exculpatory evidence.
 *
 * Adding a candidate-observed type? Add it here, not to `telemetryEventTypeSchema` — that
 * one is derived. The partition assertions at the foot of this module fail to compile if a
 * type lands in neither list or in both.
 */
export const candidateTelemetryEventTypeSchema = z.enum([
    'focus_lost',
    'focus_regained',
    'overlay_window_detected',
    'process_launched',
    'display_connected',
    'display_disconnected',
    'vm_or_remote_desktop_detected',
    'session_terminated_by_candidate',
    /*
     * The candidate's client went away without saying it meant to — a renderer reload, a
     * crash, the machine losing power.
     *
     * Separate from `session_terminated_by_candidate` because the evidence log is read as an
     * account of what the candidate *did*, and the two are not the same act. Recording a crash
     * as a deliberate departure writes an accusation into a permanent record on the strength
     * of an unload handler that cannot tell the difference; the honest row says the connection
     * ended unexpectedly and lets a human weigh it. It is `warning`, not `critical`, and it
     * does not end the session — the interviewers stay in the room and the candidate can come
     * back.
     */
    'session_disconnected_unexpectedly',
    'permission_revoked_midsession',
    'network_dropped',
    'network_rejoined',
    /*
     * Browsing activity (§3). The candidate browses freely — any site, typed or searched —
     * because the proctoring guarantee is *visibility*, not restriction: everything below is
     * on the screen share already, and these events put the same facts in the evidence log so
     * an interviewer can read the session afterwards rather than scrub a recording that does
     * not exist.
     */
    'browser_navigated',
    'browser_tab_opened',
    'browser_tab_closed',
    'browser_tab_switched',
    /*
     * Screen sharing is consent-based (§4: prevent where the OS allows, *detect and log*
     * everywhere else). The candidate starts a session not sharing and may start and stop as
     * often as they like — nothing is gated on it — so the product's obligation is to make
     * every gap attributable rather than to force the share. These two rows are what turn "the
     * screen was dark from 14:12 to 14:19" into a fact with a timestamp on both ends.
     *
     * Candidate-observed: the start/stop happens in the candidate's own capture stack, so the
     * candidate client is the only thing that can report it.
     */
    'screen_share_started',
    'screen_share_stopped',
]);
/**
 * Event types only the API may author. A candidate socket must never be able to send one.
 *
 * Kept as its own enum rather than as an omission from the candidate list, so excluding a
 * server-authored type is a positive declaration someone has to write, not an absence
 * someone has to notice.
 */
export const serverAuthoredTelemetryEventTypeSchema = z.enum([
    /*
     * Interviewer→candidate text (§5, §6). The odd one out in this taxonomy: every other type
     * is something the watchdog *observed* on the candidate machine, whereas this is something
     * the proctoring side *did*. It belongs in the same table anyway because the log is the
     * interview record, not a list of suspicions — a temporary password or an instruction
     * handed to the candidate mid-session is exactly the context that makes the rows around it
     * readable, and §6's export would otherwise show a candidate acting on something with no
     * trace of where it came from. Keeping it in one table also means one export path rather
     * than a second store that reviewers have to know to open.
     *
     * The API authors this row in the `message` handler from the *connection's* authenticated
     * identity — never from anything a client put on the wire.
     */
    'interviewer_message_sent',
]);
/** Every type the evidence log can hold, whoever authored it. Derived, never hand-edited. */
export const telemetryEventTypeSchema = z.enum([
    ...candidateTelemetryEventTypeSchema.options,
    ...serverAuthoredTelemetryEventTypeSchema.options,
]);
export const telemetrySeveritySchema = z.enum(['info', 'warning', 'critical']);
/** Which OS-level permission the candidate revoked mid-session (§4.1 preflight set). */
export const capturePermissionSchema = z.enum(['screen_recording', 'camera', 'microphone']);
/**
 * Who caused a navigation, and how.
 *
 * Worth recording because the three read very differently in an evidence log: a URL the
 * interviewer pushed is not the candidate's doing at all, a typed address is a deliberate
 * choice, and a `search` tells the interviewer the raw query is in the URL they are looking
 * at. `link` is everything else — following a link, a redirect, a form post.
 */
export const navigationSourceSchema = z.enum(['address_bar', 'search', 'link', 'interviewer']);
/**
 * Where a new tab came from: the candidate's "+" button, or a page opening one
 * (`target="_blank"`, `window.open`), which the kiosk turns into a tab rather than a window.
 */
export const tabOpenSourceSchema = z.enum(['candidate', 'page']);
/**
 * Ceiling on one interviewer-to-candidate message body.
 *
 * Generous enough for a config block or a short snippet, bounded because the body is
 * persisted verbatim in the evidence log and rendered in a panel — an unbounded field would
 * be a way to write arbitrarily large rows into `telemetry_events` over a session socket.
 *
 * Lives here rather than in `realtime.ts` because both the wire envelope and the persisted
 * event carry the same body, and `realtime.ts` already imports this module.
 */
export const MESSAGE_MAX_LENGTH = 2000;
/**
 * Why the candidate's screen share ended.
 *
 * `candidate` is the share button being pressed again — a choice, and an allowed one.
 * `track_ended` is the capture dying underneath them: the OS revoking screen-recording
 * permission, the display going away, the stream being stopped from outside the app. The
 * distinction is the whole value of the row, because only one of the two is something the
 * candidate did, and an interviewer reading the gap deserves to know which.
 */
export const screenShareStopReasonSchema = z.enum(['candidate', 'track_ended']);
const base = {
    timestamp: z.iso.datetime(),
};
/** Windows/processes the watchdog names; unnamed anomalies still report a window title. */
const windowContext = {
    processName: z.string().min(1),
    windowTitle: z.string(),
};
/**
 * Which tab in the candidate's embedded browser an event is about.
 *
 * Opaque and per-session — it identifies the tab across its own open/navigate/close story
 * without meaning anything outside that session.
 */
const tabContext = {
    tabId: z.string().min(1),
};
/**
 * The variants a candidate machine may report. Declared as its own array so the candidate
 * ingest union and the full persistence union are built from one set of member schemas
 * rather than two copies that can drift.
 */
const candidateEventMembers = [
    z.object({ ...base, type: z.literal('focus_lost'), ...windowContext }),
    z.object({ ...base, type: z.literal('focus_regained'), awayMs: z.number().int().nonnegative() }),
    z.object({
        ...base,
        type: z.literal('overlay_window_detected'),
        ...windowContext,
        /** Present in window enumeration but absent from display capture ⇒ capture-transparent. */
        captureTransparent: z.boolean(),
    }),
    z.object({
        ...base,
        type: z.literal('process_launched'),
        ...windowContext,
        pid: z.number().int(),
    }),
    z.object({
        ...base,
        type: z.literal('display_connected'),
        displayCount: z.number().int().positive(),
    }),
    z.object({
        ...base,
        type: z.literal('display_disconnected'),
        displayCount: z.number().int().nonnegative(),
    }),
    z.object({
        ...base,
        type: z.literal('vm_or_remote_desktop_detected'),
        indicator: z.string().min(1),
    }),
    z.object({ ...base, type: z.literal('session_terminated_by_candidate') }),
    z.object({ ...base, type: z.literal('session_disconnected_unexpectedly') }),
    z.object({
        ...base,
        type: z.literal('permission_revoked_midsession'),
        permission: capturePermissionSchema,
    }),
    z.object({ ...base, type: z.literal('network_dropped') }),
    z.object({
        ...base,
        type: z.literal('network_rejoined'),
        downtimeMs: z.number().int().nonnegative(),
    }),
    z.object({
        ...base,
        type: z.literal('browser_navigated'),
        ...tabContext,
        url: z.string().min(1),
        /** The page's own title; empty while the document is still loading. */
        title: z.string(),
        source: navigationSourceSchema,
        /**
         * What the candidate typed, when the omnibox turned it into a search. The query is the
         * evidence here — `https://www.google.com/search?q=...` is readable but the interviewer
         * should not have to URL-decode a log line to see what was asked.
         */
        query: z.string().optional(),
    }),
    z.object({
        ...base,
        type: z.literal('browser_tab_opened'),
        ...tabContext,
        openedBy: tabOpenSourceSchema,
        /** How many tabs exist after the open — the shape of the workspace at a glance. */
        tabCount: z.number().int().positive(),
    }),
    z.object({
        ...base,
        type: z.literal('browser_tab_closed'),
        ...tabContext,
        /** The last URL the closed tab showed, so the log does not lose it on close. */
        url: z.string(),
        tabCount: z.number().int().nonnegative(),
    }),
    z.object({
        ...base,
        type: z.literal('browser_tab_switched'),
        ...tabContext,
        url: z.string(),
    }),
    z.object({ ...base, type: z.literal('screen_share_started') }),
    z.object({
        ...base,
        type: z.literal('screen_share_stopped'),
        reason: screenShareStopReasonSchema,
    }),
];
/** The variants only the API authors. Never accepted from a client socket. */
const serverAuthoredEventMembers = [
    z.object({
        ...base,
        type: z.literal('interviewer_message_sent'),
        /** The text as the candidate received it — the log has to show what was actually sent. */
        body: z.string().min(1).max(MESSAGE_MAX_LENGTH),
        sentByUserId: z.uuid(),
        /**
         * The sender's name at send time. Denormalised on purpose: the log is a record of the
         * interview as it happened, and it must still read correctly after the account is
         * renamed or removed.
         */
        sentByName: z.string().min(1),
    }),
];
/**
 * The union the API validates candidate socket frames against (§ ingest path).
 *
 * This is the security boundary for Finding 2: a candidate with a raw WebSocket can put any
 * JSON on the wire, so the *only* thing standing between them and a forged
 * `interviewer_message_sent` row — a fabricated instruction attributed to a real
 * interviewer, in the very log used to adjudicate cheating — is that this union does not
 * contain it.
 */
export const candidateTelemetryEventSchema = z.discriminatedUnion('type', [
    ...candidateEventMembers,
]);
/** Everything the evidence log can hold: candidate-observed plus server-authored. */
export const telemetryEventSchema = z.discriminatedUnion('type', [
    ...candidateEventMembers,
    ...serverAuthoredEventMembers,
]);
export const TELEMETRY_SEVERITY = {
    focus_lost: 'warning',
    focus_regained: 'info',
    overlay_window_detected: 'critical',
    process_launched: 'warning',
    display_connected: 'critical',
    display_disconnected: 'info',
    vm_or_remote_desktop_detected: 'critical',
    session_terminated_by_candidate: 'critical',
    /*
     * `warning`, a deliberate step below its deliberate sibling. An unexpected disconnect is
     * a gap in the record that someone should account for, but it is not evidence of a choice
     * — a reload and a crash produce this row identically — and grading it `critical` would
     * put a flaky network in the same visual register as an undisclosed overlay.
     */
    session_disconnected_unexpectedly: 'warning',
    permission_revoked_midsession: 'critical',
    network_dropped: 'warning',
    network_rejoined: 'info',
    /*
     * Browsing is `info` throughout, and deliberately so: §3 makes the policy open, so a
     * candidate opening an AI site is *allowed* and colouring it as a warning would turn the
     * feed into an accusation of something we told them they could do. These rows exist to be
     * read alongside the screen share, not to be flagged.
     */
    browser_navigated: 'info',
    browser_tab_opened: 'info',
    browser_tab_closed: 'info',
    browser_tab_switched: 'info',
    /*
     * `info`, and not negotiable: this row records what the *interviewer* did. Severity in
     * this feed grades how much a candidate signal deserves attention, and an interviewer's
     * own action is not a candidate signal at all — colouring it as a warning would put the
     * proctor's message in the same visual register as an undisclosed overlay.
     */
    interviewer_message_sent: 'info',
    /*
     * Starting is `info` — the candidate doing the cooperative thing is not a finding.
     *
     * Stopping is `warning`, and the asymmetry is deliberate. Sharing is optional and stopping
     * is explicitly allowed, so `critical` would be an accusation about something the product
     * told the candidate they could do. But the interviewer is now proctoring blind, and that
     * is a fact worth surfacing in the moment rather than leaving to be discovered in the log
     * afterwards. `warning` says "notice this", not "the candidate cheated".
     */
    screen_share_started: 'info',
    screen_share_stopped: 'warning',
};
export function telemetrySeverityOf(type) {
    return TELEMETRY_SEVERITY[type];
}
/** A persisted event: the watchdog payload plus the ids the API attaches on ingest. */
export const storedTelemetryEventSchema = z.object({
    id: z.uuid(),
    interviewId: z.uuid(),
    receivedAt: z.iso.datetime(),
    event: telemetryEventSchema,
});
/**
 * Each field is `true` only if its invariant holds; anything else is a type error here, so
 * the package stops compiling. Exported (rather than left as bare unused type aliases,
 * which `noUnusedLocals` rejects) so the checks are themselves referenceable — the
 * telemetry test asserts on this value, which keeps the guarantee visible in the suite as
 * well as in the compiler.
 */
export const TELEMETRY_PARTITION_INVARIANTS = {
    exhaustive: true,
    disjoint: true,
    candidateSchemaMatchesTypes: true,
    fullSchemaMatchesTypes: true,
    candidateEventIsStorable: true,
};
/**
 * Runtime counterpart, for the one thing types cannot catch: a `z.enum` member list that
 * disagrees with the schema union it is supposed to describe. Cheap, and it runs at import.
 */
const CANDIDATE_TYPE_SET = new Set(candidateTelemetryEventTypeSchema.options);
for (const member of candidateEventMembers) {
    const type = member.shape.type.value;
    if (!CANDIDATE_TYPE_SET.has(type)) {
        throw new Error(`telemetry: '${type}' is in candidateEventMembers but not in candidateTelemetryEventTypeSchema`);
    }
}
for (const member of serverAuthoredEventMembers) {
    const type = member.shape.type.value;
    if (CANDIDATE_TYPE_SET.has(type)) {
        throw new Error(`telemetry: server-authored '${type}' must not appear in candidateTelemetryEventTypeSchema`);
    }
}
//# sourceMappingURL=telemetry.js.map